import React from 'react';
import Select from 'react-select';
import Transitions from 'material-ui/styles/transitions';
import 'core/components/fields/styledSelect.less';
import {LovItem} from "core/metamodel/mdEntity";
import {action, isArrayLike} from "mobx";
import {PropsObjectType} from "core/form/form";
import styled from "styled-components";
import {grey500} from "material-ui/styles/colors";
import SelectSearchDialog from "core/components/fields/selectSearchDialog";
import {ID} from "core/metamodel/metaConstant";
import { loc } from 'core/common/config/locales';


const InputDiv = styled.div`
    width: 100%;
    height: 100%;
    visibility: hidden;
`;

const SearchSpan = styled.span`
    position: absolute;
    bottom: 18px;
    right: 0;
    color: ${grey500};
`;

const SearchAnchor = styled.a`
  cursor: pointer;
`;


interface StyledSelectProps extends PropsObjectType {
  loadOptions?: any;
  cache?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  filterOption?: any;
  filterOptions?: any;
  id?: string;
  ignoreCase?: boolean;
  multi?: boolean;
  options?: LovItem[];
  searchable?: boolean;
  searchPromptText?: string;
  valueRenderer?: any;
  async?: boolean;
  minimumInput?: number;
  onChange?: (LovItem) => void;
  onValueEmpty?: () => void;
  asterixForAll?: boolean;  // pouze pro async, pokud je true, nastavi se filterOption na identity (pokud neexistuje) a searchPromptText na "Type to search (* for all)" (pokud neexistuje)
  advancedSearchGL?: string; // pro advanced search grid
  advancedSearchMasterId?: ID; // pro advanced search grid
  advancedSearchContentStyle?: any; // pro advanced search grid, respektive jeho dialog
  advancedSearchShowGridConfig?: boolean // pro advanced search grid
  advancedSearchTitle?: string; // pro advanced search grid
}

interface StyledSelectStyle {
  focused?: boolean;
}


/**
 * Komponenta StyledSelect v designu Material-UI.
 *
 * Komponenta pro vyber z nekolika moznosti s vyhledavanim. Podporuje mod "multi", ve kterem lze zvolit vice moznosti
 * zaroven. V "ne-multi" modu se komponenta v toku dokumentu chova jako komponenta TextField z material UI.
 *
 * V modu "multi" je komponenta dostylovana tak aby zapadala do material designu. V takovem pripade ma nastavenou
 * vysku na automatickou (height: auto) a minimalni vysku na 48px (min-height: 48px). Pro praci s velikosti kopmonenty
 * je treba prekryt minimalni vysku komponenty (min-height) namisto klasicke vysky (height), jak je tomu u TextField
 * z material-ui.
 *
 */
export default class StyledSelect extends React.PureComponent<StyledSelectProps, StyledSelectStyle> {


  static defaultProps: StyledSelectProps = {
    minimumInput: 1,
    cache: false
  };


  _uniqueId: string;

  private selectSearchDialogRef: SelectSearchDialog;


  constructor(props) {
    super(props);

    this.state = {
      focused: false
    }
  }

  componentDidMount() {
    let {name, floatingLabelText} = this.props;
    this._uniqueId = (name + '-' + floatingLabelText + '-' + Math.floor(Math.random() * 0xFFFF)).replace(/[^A-Za-z0-9-]/gi, '');

  }


  onBlurEvent = (e) => {
    this.setState({focused: false});
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  onFocusEvent = (e) => {
    this.setState({focused: true});
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  onChangeEvent = (value) => {
    value = ( (value && value.value === null && value.label === '---') || (!this.props.multi && isArrayLike(value)) ) ? null : value;  // to || (!this.props.multi &&isArray(value)) je https://github.com/JedWatson/react-select/issues/1215
    if (this.props.onChange) {
      this.props.onChange(value);
    }
    if (this.props.onValueEmpty && !value) {
      this.props.onValueEmpty();
    }
  };

  onSearch = () => {
    console.log('onSearch');
    new Promise((resolve, reject) => {
      return this.selectSearchDialogRef.open(resolve, reject);
    }).then(action("close-new-party-diaglog", (foundPartyLov: LovItem) => {
      this.onChangeEvent(foundPartyLov);
    })).catch( (e) => {});
  };

  identity = a => a;

  selectSearchDialogRefHandler = (comp) => this.selectSearchDialogRef = comp;

  render() {

    let {
      errorText,
      loadOptions,
      cache,
      clearable,
      disabled,
      filterOption,
      filterOptions,
      floatingLabelStyle,
      floatingLabelText,
      id,
      ignoreCase,
      multi,
      name,
      options,
      searchable,
      searchPromptText,
      value,
      valueRenderer,
      async,
      minimumInput,
      hintText,
      asterixForAll,
      fullWidth,
      mdField,
      advancedSearchGL,
      advancedSearchMasterId,
      advancedSearchContentStyle,
      advancedSearchShowGridConfig,
      advancedSearchTitle,
      ...other
      } = this.props;


    let inputId = id || this._uniqueId;

    let styles = {
      root: {
        fontSize: 16,
        lineHeight: '24px',
        width: fullWidth ? '100%' : 256,
        height: floatingLabelText ? 72 : 48,
        display: 'inline-block',
        position: 'relative',
        fontFamily: 'Roboto+Condensed, sans-serif',
        transition: Transitions.easeOut('200ms', 'height'),
      }
    };


    let selectClassName = "StyledSelect";
    if (this.state.focused) {
      selectClassName += " is-focused";
    }
    if ((value && (value.toString().length > 0))) {
      selectClassName += " has-value";
    }
    if (errorText) {
      selectClassName += " is-error";
    }
    if (multi) {
      selectClassName += " is-multi";
    }
    if (async) {
      selectClassName += " is-async";
    }

    if (isArrayLike(options) && !Array.isArray(options)) {
      options = (options as any).slice();
    }

    if (isArrayLike(value) && !Array.isArray(value)) {
      value = (value as any).slice();
    }

    if (async && asterixForAll) {
      if (!searchPromptText) searchPromptText = loc.get("search.hintText");
      if (!filterOption) filterOption = this.identity;
    }

    // <input className="rowAligner" type="text" style={{width: '100%', height: '100%', visibility: 'hidden'}} />

    let floatingLabelTextElement = floatingLabelText ? (
      <label className="SelectLabel"
             htmlFor={inputId}
             style={floatingLabelStyle}>
        {floatingLabelText}
      </label>) : null;

    let nativeStyle = {};
    if (advancedSearchGL) {
      clearable = false;
      nativeStyle["width"] = "95%";
    }

    return (
      <div className={selectClassName} style={Object.assign(styles.root, this.props.style)}>
        {floatingLabelTextElement}
        <InputDiv className="rowAligner" type="text"/>
        {(advancedSearchGL) ? <SearchAnchor onClick={this.onSearch}> <SearchSpan className="fa fa-search"/> </SearchAnchor> : null}
        {(advancedSearchGL) ? ( <SelectSearchDialog ref={this.selectSearchDialogRefHandler} gridLocation={advancedSearchGL} masterId={advancedSearchMasterId} contentStyle={advancedSearchContentStyle} showGridConfig={advancedSearchShowGridConfig} title={advancedSearchTitle} />) : null}

        {
          (async) ?
            <Select.Async onFocus={this.onFocusEvent} onBlur={this.onBlurEvent}
                          onChange={this.onChangeEvent}
                          name={name}
                          valueRenderer={valueRenderer}
                          value={value}
                          multi={multi}
                          loadOptions={loadOptions}
                          disabled={disabled}
                          clearable={clearable}
                          minimumInput={minimumInput}
                          autoload={minimumInput === 0}
                          cache={cache}
                          filterOptions={filterOptions}
                          filterOption={filterOption}
                          searchPromptText={searchPromptText}
                          placeholder={hintText}
                          ignoreAccents={false}
                          wrapperStyle={nativeStyle}
            />
            :
            <Select onFocus={this.onFocusEvent} onBlur={this.onBlurEvent} onChange={this.onChangeEvent}
                    name={name}
                    valueRenderer={valueRenderer}
                    value={value}
                    options={options}
                    clearable={clearable}
                    searchable={searchable}
                    multi={multi}
                    disabled={disabled}
                    placeholder={hintText}
                    matchProp="label"
                    style={nativeStyle}
            />

        }
        <hr className="underscore-grey"/>
        <hr className="underscore-blue"/>
        <div className="errorText">{errorText}</div>
      </div>
    )
  }


}



// WEBPACK FOOTER //
// ./src/core/components/fields/styledSelect.tsx